import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Trash2, Save, X, IndianRupee } from "lucide-react";
import AdminShell from "@/components/AdminShell";
import api from "@/lib/api";
import { toast } from "sonner";

const EMPTY = {
    code: "",
    name: "",
    description: "",
    duration_min: 60,
    amount_inr: 2499,
    currency: "INR",
    active: true,
    featured: false,
};

function ConsultationForm({ initial, onSave, onCancel, isNew }) {
    const [form, setForm] = useState(initial);
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => {
        const v = e.target.type === "checkbox" ? e.target.checked : e.target.type === "number" ? Number(e.target.value) : e.target.value;
        setForm((s) => ({ ...s, [k]: v }));
    };

    const save = async (e) => {
        e?.preventDefault?.();
        if (!form.code || !form.name || !form.amount_inr) {
            return toast.error("code, name and amount are required");
        }
        setBusy(true);
        try {
            const payload = {
                ...form,
                amount: Math.round(form.amount_inr * 100),
            };
            delete payload.amount_inr;
            if (isNew) {
                await api.post("/admin/consultations", payload);
            } else {
                const { code, ...rest } = payload;
                await api.patch(`/admin/consultations/${initial.code}`, rest);
            }
            toast.success("Saved");
            onSave();
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={save} className="card-surface p-6 lg:p-8 space-y-6" data-testid="consultation-form">
            <div className="flex items-start justify-between">
                <div>
                    <div className="eyebrow text-saffron-brand mb-1">{isNew ? "New offering" : "Edit offering"}</div>
                    <div className="font-display text-xl">Dr. Aangan Theory consultation</div>
                </div>
                <button type="button" onClick={onCancel} className="text-white/55 hover:text-white">
                    <X size={18} />
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                <div>
                    <span className="field-label">Code (unique, no spaces)</span>
                    <input className="field-input" value={form.code} onChange={set("code")} disabled={!isNew} placeholder="doctor_consultation" data-testid="con-input-code" />
                </div>
                <div>
                    <span className="field-label">Display name</span>
                    <input className="field-input" value={form.name} onChange={set("name")} placeholder="Dr. Aangan Theory · Brand Diagnosis" data-testid="con-input-name" />
                </div>
                <div className="md:col-span-2">
                    <span className="field-label">Description</span>
                    <textarea rows={2} className="field-input" value={form.description} onChange={set("description")} data-testid="con-input-description" />
                </div>
                <div>
                    <span className="field-label">Duration (minutes)</span>
                    <input type="number" className="field-input" value={form.duration_min} onChange={set("duration_min")} data-testid="con-input-duration" />
                </div>
                <div>
                    <span className="field-label">Price (₹ INR)</span>
                    <div className="flex items-center gap-2">
                        <IndianRupee size={14} className="text-saffron-brand" />
                        <input type="number" min="0" step="1" className="field-input" value={form.amount_inr} onChange={set("amount_inr")} data-testid="con-input-amount" />
                    </div>
                </div>
                <label className="flex items-center gap-3 text-sm text-white/80 cursor-pointer mt-2">
                    <input type="checkbox" checked={form.active} onChange={set("active")} className="accent-saffron" data-testid="con-input-active" />
                    Active (visible on website)
                </label>
                <label className="flex items-center gap-3 text-sm text-white/80 cursor-pointer mt-2">
                    <input type="checkbox" checked={form.featured} onChange={set("featured")} className="accent-saffron" data-testid="con-input-featured" />
                    Featured (highlighted on Doctor page)
                </label>
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/8">
                <button type="button" onClick={onCancel} className="btn-secondary text-xs">Cancel</button>
                <button type="submit" disabled={busy} className="btn-primary text-xs" data-testid="con-save-btn">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    {isNew ? "Create offering" : "Save changes"}
                </button>
            </div>
        </form>
    );
}

export default function AdminConsultations() {
    const [rows, setRows] = useState(null);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);

    const load = useCallback(async () => {
        const { data } = await api.get("/admin/consultations");
        setRows(data);
    }, []);

    useEffect(() => { load(); }, [load]);

    const remove = async (code) => {
        if (!window.confirm(`Delete '${code}'? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/consultations/${code}`);
            toast.success("Deleted");
            load();
        } catch (err) {
            toast.error("Delete failed");
        }
    };

    return (
        <AdminShell title="Dr. Aangan Pricing">
            <div className="flex items-center justify-between mb-8">
                <p className="text-white/55 text-sm max-w-2xl">
                    Manage the consultation offerings shown on the public Dr. Aangan Theory page. Pricing changes apply immediately to new bookings.
                </p>
                {!creating && !editing && (
                    <button onClick={() => setCreating(true)} className="btn-primary text-xs" data-testid="con-add-btn">
                        <Plus size={14} /> Add offering
                    </button>
                )}
            </div>

            {creating && (
                <div className="mb-8">
                    <ConsultationForm
                        isNew
                        initial={EMPTY}
                        onCancel={() => setCreating(false)}
                        onSave={() => { setCreating(false); load(); }}
                    />
                </div>
            )}

            {editing && (
                <div className="mb-8">
                    <ConsultationForm
                        initial={{ ...editing, amount_inr: editing.amount / 100 }}
                        onCancel={() => setEditing(null)}
                        onSave={() => { setEditing(null); load(); }}
                    />
                </div>
            )}

            {rows === null ? (
                <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-saffron" size={20} /></div>
            ) : rows.length === 0 ? (
                <div className="card-surface p-12 text-center">
                    <div className="eyebrow mb-3">Nothing here yet</div>
                    <h3 className="font-display text-xl mb-4">Add your first Dr. Aangan offering</h3>
                </div>
            ) : (
                <div className="space-y-3">
                    {rows.map((r) => (
                        <div key={r.code} className="card-surface p-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-center" data-testid={`con-row-${r.code}`}>
                            <div className="md:col-span-3">
                                <div className="eyebrow mb-1">{r.code}</div>
                                <div className="font-display text-base text-white">{r.name}</div>
                            </div>
                            <div className="md:col-span-4 text-white/65 text-sm">{r.description}</div>
                            <div className="md:col-span-2 font-mono text-2xl text-saffron-brand">₹{(r.amount / 100).toLocaleString("en-IN")}</div>
                            <div className="md:col-span-1">
                                <span className={`tag ${r.active ? "is-active" : ""}`}>{r.active ? "Live" : "Hidden"}</span>
                            </div>
                            <div className="md:col-span-2 flex md:justify-end gap-2">
                                <button onClick={() => setEditing(r)} className="btn-secondary text-xs" data-testid={`con-edit-${r.code}`}>Edit</button>
                                <button onClick={() => remove(r.code)} className="btn-secondary text-xs hover:!border-red-400 hover:!text-red-400" data-testid={`con-delete-${r.code}`}>
                                    <Trash2 size={12} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </AdminShell>
    );
}
